import {
  createEffect,
  createMemo,
  createSignal,
  onCleanup,
  onMount,
  Show,
  Suspense,
} from "solid-js";
import {
  Outlet,
  useLocation,
  useNavigate,
  useRouterState,
  useSearch,
} from "@tanstack/solid-router";
import type { AbstractPowerSyncDatabase } from "@powersync/common";
import type { MessageDescriptor } from "@lingui/core";

import { type ListGroup, type ListWithMeta, mapSmartViewCounts } from "@listto/shared";
import { useSmartViewCountsQuery } from "@listto/shared/solid";

import { useDrizzle } from "@/hooks/use-drizzle";
import { useSession } from "@/lib/auth";
import { appState } from "@fleet/ui";
import { useTheme } from "@/providers/theme-provider";
import { useI18n } from "@/providers/i18n-provider";
import { useSearchPalette } from "@/features/search/hooks/use-search-palette";

// Layout specific components and providers
import { AppBackground } from "@/components/app-background";
import { AppHeader } from "@/components/app-header";
import { OfflineBanner } from "@/components/offline-banner";
import { ListThemeProvider } from "@/providers/list-theme-provider";
import { TodoDetailRouteView } from "@/features/todos/components/todo-detail-route-view";
import { CommandPalette } from "@/features/search/components/command-palette";
import { AppErrorBoundary } from "@/components/app-error-boundary";

// Layout module parts
import { useInteractionFlag } from "@fleet/ui";
import { AppLayoutContext } from "../context";
import { SwipeIndicators } from "./swipe-indicators";
import { SwipePreviewBackground } from "./swipe-preview-background";
import { AppLayoutSidebar } from "./app-layout-sidebar";
import { AppLayoutDialogs } from "./app-layout-dialogs";
import { usePageSwipeGestures } from "../hooks/use-page-swipe-gestures";
import { useAppDialogs } from "../hooks/use-app-dialogs";
import { useAppThemeState } from "../hooks/use-app-theme-state";
import { useAppShortcuts } from "../hooks/use-app-shortcuts";
import {
  getListNameForPath,
  getOrderedListPaths,
  getSwipePreviewPath,
  type PreviewListTheme,
  resolveListThemeForPath,
  type SidebarQueryResult,
} from "../helpers";
import type { AppLayoutSearch } from "../app-search";
import { OFFLINE_CACHE_STALE_TIME_MS } from "@/lib/constants";

interface AppLayoutContentProps {
  db: AbstractPowerSyncDatabase | null;
  activeListId: string | null;
  activeDragId: () => string | null;
  lists: SidebarQueryResult<ListWithMeta>;
  listGroups: SidebarQueryResult<ListGroup>;
}

function getAdjacentListNamesHelper(
  paths: string[],
  currentIndex: number,
  lists: ListWithMeta[] | undefined,
  t: (message: MessageDescriptor) => string,
) {
  if (paths.length === 0 || currentIndex === -1) {
    return { prev: "", next: "" };
  }
  const nextIndex = (currentIndex + 1) % paths.length;
  const prevIndex = (currentIndex - 1 + paths.length) % paths.length;
  const listData = lists ?? [];

  return {
    prev: getListNameForPath(paths[prevIndex] ?? "", t, listData),
    next: getListNameForPath(paths[nextIndex] ?? "", t, listData),
  };
}

function getNextPath(isNext: boolean, paths: string[], currentIndex: number): string | undefined {
  if (paths.length === 0 || currentIndex === -1) {
    return undefined;
  }
  const nextIndex = isNext
    ? (currentIndex + 1) % paths.length
    : (currentIndex - 1 + paths.length) % paths.length;
  return paths[nextIndex];
}

function getCurrentPathIndex(path: string, orderedListPaths: string[]): number {
  if (path.includes("/todo/")) {
    return -1;
  }
  return orderedListPaths.indexOf(path);
}

function getListDialogProps(list: ListWithMeta | null | undefined) {
  if (!list) {
    return undefined;
  }
  return {
    id: list.id,
    name: list.name,
    color: list.color,
    icon: list.icon,
    theme_id: list.theme_id,
    is_blur: list.is_blur,
  };
}

function useGlobalEventListeners(
  isSidebarOpen: () => boolean,
  setIsSidebarOpen: (open: boolean) => void,
) {
  // Keyboard-vs-pointer activity tracking (and the `body.keyboard-navigation`
  // class) is owned by <InteractionProvider>; only the sidebar-specific Escape
  // handler remains here.
  onMount(() => {
    const handleEscapeForSidebar = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isSidebarOpen()) {
        setIsSidebarOpen(false);
      }
    };

    globalThis.addEventListener("keydown", handleEscapeForSidebar);

    onCleanup(() => {
      globalThis.removeEventListener("keydown", handleEscapeForSidebar);
    });
  });
}

export function AppLayoutContent(props: AppLayoutContentProps) {
  onMount(() => {
    // Notify the InitialSyncOverlay that we are mounted and ready
    appState.markAsReady();
  });

  const navigate = useNavigate();
  const searchPalette = useSearchPalette();
  const { t } = useI18n();

  const rawLocation = useLocation();
  const routerState = useRouterState();

  // In-app paths are BARE (`/inbox`, `/lists/<id>`): the URL names a resource
  // and nothing else, since WHICH account a tab shows is per-tab state in
  // `sessionStorage`, not a path segment. The memo is kept as the single place
  // downstream path logic reads its location from.
  const location = createMemo(() => rawLocation());

  // Track the target path during fast keyboard navigation to avoid lag
  const [navigationTargetPath, setNavigationTargetPath] = createSignal<string | null>(null);

  createEffect(() => {
    const currentPath = location().pathname;
    const target = navigationTargetPath();
    if (currentPath === target || routerState().status !== "pending") {
      setNavigationTargetPath(null);
    }
  });

  // Section key for the per-route entrance animation (slide-up + fade on mount).
  // We strip the `/todo/$todoId` detail suffix so opening/closing the detail
  // drawer (which renders in its own layer, leaving the parent route mounted)
  // does NOT replay the parent page's entrance. Changing this key remounts the
  // keyed wrapper around the <Outlet>, re-triggering the CSS keyframe.
  const routeSectionKey = createMemo(
    () => location().pathname.replace(/\/todo\/[^/]+\/?$/, "") || "/",
  );

  const orderedListPaths = createMemo(() =>
    getOrderedListPaths(props.lists.data, props.listGroups.data),
  );

  const currentPathIndex = () => {
    const current = navigationTargetPath() ?? location().pathname;
    return getCurrentPathIndex(current, orderedListPaths());
  };

  const getAdjacentListNames = () =>
    getAdjacentListNamesHelper(orderedListPaths(), currentPathIndex(), props.lists.data, t);

  const navigateList = (isNext: boolean) => {
    const targetPath = getNextPath(isNext, orderedListPaths(), currentPathIndex());
    if (targetPath) {
      setNavigationTargetPath(targetPath);
      void navigate({ to: targetPath });
    }
  };

  const {
    swipeListTx,
    isSwipingList,
    swipeListTy,
    isSwipingListY,
    swipeListAnimating,
    swipeListYAnimating,
    LIST_SWIPE_INDICATOR_TRIGGER_PX,
    LIST_SWIPE_COMMIT_THRESHOLD_PX,
    handlers: pageSwipeHandlers,
  } = usePageSwipeGestures({
    location,
    navigate,
    orderedListPaths,
    currentPathIndex,
    navigateList,
  });

  const isNavigating = () => routerState().status === "pending";

  const drizzle = useDrizzle();
  const session = useSession();
  const { userId } = session;

  const theme = useTheme();

  const {
    listDialogOpen,
    setListDialogOpen,
    creationGroupId,
    setCreationGroupId,
    shareDialogOpen,
    setShareDialogOpen,
    selectedList,
    setSelectedList,
    isSidebarOpen,
    setIsSidebarOpen,
    choiceDialogOpen,
    setChoiceDialogOpen,
    groupDialogOpen,
    setGroupDialogOpen,
    editingGroup,
    setEditingGroup,
    shortcutsOpen,
    setShortcutsOpen,
    // `location` is passed by reference (the accessor itself), not read here;
    // `useAppDialogs` tracks it internally. The rule can't see through the
    // positional arg, so the read-outside-tracked-scope warning is a false positive.
    // oxlint-disable-next-line solid/reactivity
  } = useAppDialogs(location);

  // ── URL-addressability of global overlays (#15) ───────────────────────────
  // The URL is the source of truth for which overlay is open. Each `open*`
  // navigates (replace) to write the search-param; a `createEffect` reflects
  // URL→signals (resolves entities by id from already loaded data). This way
  // pasting the URL in another browser reopens the exact same dialog, and
  // back/forward closes/opens it. Child route search params (e.g. `/?q=`)
  // are preserved because we only write layout keys.
  const layoutSearch = useSearch({ from: "/_app" });

  const setLayoutSearch = (patch: Partial<AppLayoutSearch>) => {
    void navigate({
      to: ".",
      search: (prev) => {
        // See the note in `audit-log-viewer.tsx`: `prev` is the strict search
        // schema, so the patch loop widens to a mutable record and re-narrows.
        const next: Record<string, unknown> = { ...prev };
        for (const [k, v] of Object.entries(patch)) {
          if (v === undefined) {
            delete next[k];
          } else {
            next[k] = v;
          }
        }
        // oxlint-disable-next-line typescript/no-unsafe-type-assertion
        return next as typeof prev;
      },
      replace: true,
    });
  };

  // ── Command palette: the URL is the source of truth for its open-state ──
  // Other components (header, shortcuts, todo-list-view) open/close via the
  // singleton `useSearchPalette`. We bridge singleton→URL so that any
  // opening is reflected (copyable), and URL→singleton (below) so that
  // a pasted URL / back-forward reopens the palette. A guard prevents loops.
  let syncingPalette = false;
  let paletteBridgeReady = false;
  createEffect(() => {
    const open = searchPalette.isOpen();
    // On the first run the URL is the source of truth (the URL→singleton effect
    // below seeds the singleton); we do not write the URL until then to
    // avoid wiping a freshly pasted `?palette=1`.
    if (!paletteBridgeReady) {
      paletteBridgeReady = true;
      return;
    }
    if (syncingPalette) {
      return;
    }
    const current = Boolean(layoutSearch().palette);
    if (open === current) {
      return;
    }
    if (open) {
      setLayoutSearch({ palette: true });
    } else {
      setLayoutSearch({ palette: undefined, pq: undefined, pf: undefined });
    }
  });

  const handleCloseSearch = () => {
    searchPalette.close();
    setLayoutSearch({ palette: undefined, pq: undefined, pf: undefined });
  };

  // Palette query/filter reflected to the URL (copyable). Different from `/?q=`
  // (dashboard), which searches lists/folders; the palette searches todos+lists.
  const handlePaletteQueryChange = (q: string) => {
    setLayoutSearch({ pq: q.trim() ? q : undefined, palette: true });
  };
  const handlePaletteFilterChange = (f: "all" | "todo" | "list") => {
    setLayoutSearch({ pf: f === "all" ? undefined : f, palette: true });
  };

  // ── Handlers that write the URL (replace imperative `open*`) ──
  const openChoiceDialog = (groupId?: string) => {
    setLayoutSearch({
      dialog: "choice",
      groupId: groupId ?? undefined,
      listId: undefined,
      modal: undefined,
    });
  };
  const openNewListDialog = (groupId?: string) => {
    setLayoutSearch({
      dialog: "list-new",
      groupId: groupId ?? undefined,
      listId: undefined,
      modal: undefined,
    });
  };
  const openNewGroupDialog = () => {
    setLayoutSearch({
      dialog: "group-new",
      listId: undefined,
      groupId: undefined,
      modal: undefined,
    });
  };
  const openEditListDialog = (list: ListWithMeta) => {
    setLayoutSearch({ dialog: "list-edit", listId: list.id, groupId: undefined, modal: undefined });
  };
  const openShareListDialog = (list: ListWithMeta) => {
    setLayoutSearch({ dialog: "share", listId: list.id, groupId: undefined, modal: undefined });
  };
  const openEditGroupDialog = (group: ListGroup) => {
    setLayoutSearch({
      dialog: "group-edit",
      groupId: group.id,
      listId: undefined,
      modal: undefined,
    });
  };
  const closeAllDialogs = () => {
    setLayoutSearch({ dialog: undefined, listId: undefined, groupId: undefined });
  };
  const setShortcutsModalOpen = (open: boolean) => {
    setLayoutSearch({ modal: open ? "shortcuts" : undefined });
  };

  // onOpenChange shared by global Dialogs: on close (false), clears
  // search-params; Dialogs never emit `true` on their own (they open via
  // the `open*` handlers that write the URL), so we ignore `true`.
  const dialogOnOpenChange = (open: boolean) => {
    if (!open) {
      closeAllDialogs();
    }
  };

  // ── URL → signals (init + back/forward + pasted link) ──
  createEffect(() => {
    const s = layoutSearch();
    const { dialog } = s;

    // Entity resolution by id from already loaded layout data. If the
    // id does not exist / without access, the dialog does NOT open (graceful degradation: do not show
    // a broken/empty form). Lists/groups come filtered by sync rules.
    const findList = (id: string | undefined) =>
      id ? (props.lists.data?.find((l) => l.id === id) ?? null) : null;
    const findGroup = (id: string | undefined) =>
      id ? (props.listGroups.data?.find((g) => g.id === id) ?? null) : null;

    // Choice
    setChoiceDialogOpen(dialog === "choice");

    // List dialog (new | edit)
    if (dialog === "list-new") {
      setSelectedList(null);
      setCreationGroupId(s.groupId);
      setListDialogOpen(true);
    } else if (dialog === "list-edit") {
      const list = findList(s.listId);
      // Keep open if the list has not loaded yet (may arrive via sync); close it
      // only if data is ready and the list does not exist.
      const listsReady = !props.lists.isPending;
      if (list) {
        setSelectedList(list);
        setCreationGroupId(undefined);
        setListDialogOpen(true);
      } else if (listsReady) {
        setListDialogOpen(false);
      }
    } else {
      setCreationGroupId(undefined);
      setListDialogOpen(false);
    }

    // Group dialog (new | edit)
    if (dialog === "group-new") {
      setEditingGroup(null);
      setGroupDialogOpen(true);
    } else if (dialog === "group-edit") {
      const group = findGroup(s.groupId);
      const groupsReady = !props.listGroups.isPending;
      if (group) {
        setEditingGroup(group);
        setGroupDialogOpen(true);
      } else if (groupsReady) {
        setGroupDialogOpen(false);
      }
    } else {
      setGroupDialogOpen(false);
    }

    // Share dialog
    if (dialog === "share") {
      const list = findList(s.listId);
      const listsReady = !props.lists.isPending;
      if (list) {
        setSelectedList(list);
        setShareDialogOpen(true);
      } else if (listsReady) {
        setShareDialogOpen(false);
      }
    } else {
      setShareDialogOpen(false);
    }

    // Shortcuts modal
    setShortcutsOpen(s.modal === "shortcuts");

    // Command palette open-state mirrored to the singleton (URL→singleton).
    // Guarded so the singleton→URL bridge above doesn't re-fire and loop.
    syncingPalette = true;
    if (s.palette) {
      searchPalette.open();
    } else {
      searchPalette.close();
    }
    queueMicrotask(() => {
      syncingPalette = false;
    });
  });

  useAppShortcuts({
    searchPalette,
    location,
    navigate,
    theme,
    t,
    listDialogOpen,
    choiceDialogOpen,
    groupDialogOpen,
    shareDialogOpen,
    shortcutsOpen,
    setShortcutsOpen: setShortcutsModalOpen,
    setIsSidebarOpen,
    openNewListDialog,
    openNewGroupDialog,
    navigateList,
  });

  // ── Keyboard-navigation visual indicator + touch gestures
  useGlobalEventListeners(isSidebarOpen, setIsSidebarOpen);

  // With the nav drawer open, the horizontal gesture belongs to it (drag to
  // dismiss). The page swipe listens on the window and would otherwise fire
  // underneath it, so ownership is announced in the interaction context rather
  // than re-derived from the DOM by each gesture.
  useInteractionFlag({ "nav-drawer.open": isSidebarOpen });

  const counts = useSmartViewCountsQuery({
    db: () => props.db,
    drizzle,
    userId,
    staleTime: OFFLINE_CACHE_STALE_TIME_MS,
  });

  const stats = () => {
    if (counts.isLoading) {
      return undefined;
    }
    const { data } = counts;
    const s = mapSmartViewCounts(data ?? undefined);
    return {
      all: s.all,
      myDay: s.myDay,
      important: s.important,
      planned: s.planned,
      inbox: s.inbox,
    };
  };

  const {
    listContextId,
    activeTodoId,
    backToRoute,
    backParams,
    activeBlur,
    activeListTokens,
    activeListBackground,
    glows,
  } = useAppThemeState({
    committedPath: () => location().pathname,
    listsData: () => props.lists.data,
    theme,
  });

  const listDialogProps = createMemo(() => getListDialogProps(selectedList()));

  // ── List-swipe carousel: preview the destination list's theme ──────────────
  // While dragging horizontally, resolve the background/pattern/glows of the
  // list "under the finger" so `SwipePreviewBackground` can crossfade it in —
  // the panel's theme arrives smoothly instead of popping once the route
  // change lands. `swipePreviewPath` is null whenever there is nothing to
  // preview (idle, single list, or a route outside the ordered set).
  const swipePreviewPath = createMemo(() =>
    getSwipePreviewPath(swipeListTx(), orderedListPaths(), currentPathIndex()),
  );

  const swipePreviewTheme = createMemo<PreviewListTheme | null>(() => {
    const path = swipePreviewPath();
    if (!path) {
      return null;
    }
    return resolveListThemeForPath(path, {
      listsData: props.lists.data,
      allThemes: theme.allThemes(),
      parentTokens: theme.tokens(),
      defaultIsBlur: theme.isBlur(),
      resolveScopedTokens: theme.resolveScopedTokens,
    });
  });

  const swipePreviewOpacity = createMemo(() => {
    if (!swipePreviewPath()) {
      return 0;
    }
    return Math.min(Math.abs(swipeListTx()) / LIST_SWIPE_COMMIT_THRESHOLD_PX, 1);
  });

  return (
    <ListThemeProvider listId={listContextId()}>
      <AppBackground
        class="flex h-dvh text-foreground overflow-hidden"
        isBlur={activeBlur()}
        background={activeListBackground()}
        patternType={activeListTokens()?.patternType ?? theme.tokens()?.patternType}
        patternOpacity={activeListTokens()?.patternOpacity ?? theme.tokens()?.patternOpacity}
        {...glows()}
        previewLayer={
          <SwipePreviewBackground
            theme={swipePreviewTheme}
            opacity={swipePreviewOpacity}
            animating={swipeListAnimating}
            isBlur={activeBlur}
          />
        }
      >
        <SwipeIndicators
          isSwipingList={isSwipingList}
          swipeListTx={swipeListTx}
          isSwipingListY={isSwipingListY}
          swipeListTy={swipeListTy}
          LIST_SWIPE_INDICATOR_TRIGGER_PX={LIST_SWIPE_INDICATOR_TRIGGER_PX}
          LIST_SWIPE_COMMIT_THRESHOLD_PX={LIST_SWIPE_COMMIT_THRESHOLD_PX}
          location={location}
          getAdjacentListNames={getAdjacentListNames}
          t={t}
        />

        <AppLayoutSidebar
          isOpen={isSidebarOpen}
          setOpen={setIsSidebarOpen}
          stats={stats}
          statsLoading={() => stats() === undefined}
        />

        {/* ── Main content ───────────────────────────────────────── */}
        <main
          class="flex flex-1 flex-col overflow-hidden bg-transparent pt-[var(--safe-area-inset-top)] pr-[var(--safe-area-inset-right)] pb-[var(--safe-area-inset-bottom)]"
          {...pageSwipeHandlers}
        >
          <AppHeader onMenuClick={() => setIsSidebarOpen(true)} isNavigating={isNavigating} />
          <OfflineBanner />
          <div
            class="flex flex-1 flex-col min-h-0 overflow-hidden pr-[var(--safe-area-inset-right)] pl-[var(--safe-area-inset-left)]"
            style={{
              transform: `translate3d(${swipeListTx()}px, ${swipeListTy()}px, 0)`,
              transition:
                swipeListAnimating() || swipeListYAnimating()
                  ? "transform 240ms cubic-bezier(0.32, 0.72, 0, 1)"
                  : "none",
              "will-change":
                isSwipingList() || isSwipingListY() || swipeListAnimating() || swipeListYAnimating()
                  ? "transform"
                  : "auto",
            }}
          >
            <AppLayoutContext.Provider
              value={{
                openNewListDialog,
                openChoiceDialog,
                openEditListDialog,
                openShareListDialog,
                openEditGroupDialog,
                activeDragId: () => props.activeDragId(),
              }}
            >
              <div class="flex flex-1 flex-col min-h-0 h-full">
                {/* Inline guard: a render error in the active route keeps the
                    shell (sidebar/header) intact and offers Retry/Reload. */}
                <AppErrorBoundary inline label="route">
                  {/* Per-route entrance: keying on the section path remounts this
                      wrapper on navigation, replaying `slide-in-up` (GPU-only:
                      transform + opacity). Detail-drawer opens don't change the
                      key, so they don't re-trigger it. `prefers-reduced-motion`
                      is honored globally in global.css. */}
                  <Show when={routeSectionKey()} keyed>
                    <div class="flex flex-1 flex-col min-h-0 h-full animate-slide-in-up will-change-[transform,opacity]">
                      <Outlet />
                    </div>
                  </Show>
                </AppErrorBoundary>
              </div>
            </AppLayoutContext.Provider>
          </div>
        </main>

        <AppLayoutDialogs
          listDialogOpen={listDialogOpen}
          setListDialogOpen={dialogOnOpenChange}
          listDialogProps={listDialogProps}
          creationGroupId={creationGroupId}
          choiceDialogOpen={choiceDialogOpen}
          setChoiceDialogOpen={dialogOnOpenChange}
          openNewListDialog={openNewListDialog}
          openNewGroupDialog={openNewGroupDialog}
          groupDialogOpen={groupDialogOpen}
          setGroupDialogOpen={dialogOnOpenChange}
          editingGroup={editingGroup}
          shareDialogOpen={shareDialogOpen}
          setShareDialogOpen={dialogOnOpenChange}
          selectedList={selectedList}
          shortcutsOpen={shortcutsOpen}
          setShortcutsOpen={setShortcutsModalOpen}
        />

        {/* Persistent Global Details Drawer */}
        <Show when={Boolean(activeTodoId())}>
          <Suspense fallback={null}>
            <TodoDetailRouteView
              todoId={activeTodoId() ?? ""}
              backTo={backToRoute()}
              backParams={backParams()}
            />
          </Suspense>
        </Show>
      </AppBackground>

      {/* Command Palette — rendered OUTSIDE AppBackground to avoid blur compositor flash */}
      <CommandPalette
        isOpen={searchPalette.isOpen()}
        onClose={handleCloseSearch}
        query={layoutSearch().pq}
        filter={layoutSearch().pf ?? "all"}
        onQueryChange={handlePaletteQueryChange}
        onFilterChange={handlePaletteFilterChange}
      />
    </ListThemeProvider>
  );
}
